import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { prChipStyle, prStateSummary } from './prPresentation';
import { getMyReviews } from '../../utils/shapeUpApi';

/**
 * LocalStorage key for persisting the expanded/collapsed state of the My Reviews
 * accordion. The user's preference persists across sessions.
 */
const EXPANDED_KEY = 'shapeup-my-reviews-expanded';

/**
 * A dedicated section showing all PRs needing the current user's review.
 * Designed to be placed prominently at the top of the cycle view to make it
 * "noisy" when reviews are pending.
 */
export default function MyReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => {
    const stored = localStorage.getItem(EXPANDED_KEY);
    return stored !== null ? stored === 'true' : true;
  });

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        const data = await getMyReviews();
        if (!cancelled) {
          setReviews(data.reviews || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load your reviews.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, []);

  const handleExpandChange = (_, isExpanded) => {
    setExpanded(isExpanded);
    localStorage.setItem(EXPANDED_KEY, String(isExpanded));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading your reviews...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  // Group reviews by bet for better organization
  const byBet = new Map();
  for (const review of reviews) {
    const key = review.betId;
    if (!byBet.has(key)) {
      byBet.set(key, {
        betId: review.betId,
        betName: review.betName,
        cycleId: review.cycleId,
        cycleName: review.cycleName,
        reviews: [],
      });
    }
    byBet.get(key).reviews.push(review);
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpandChange}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'warning.main',
        bgcolor: 'warning.dark',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <RateReviewIcon sx={{ color: 'warning.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {reviews.length} PR{reviews.length === 1 ? '' : 's'} Awaiting Your Review
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={2}>
          {[...byBet.values()].map((group) => (
            <Box key={group.betId}>
              <Link
                component={RouterLink}
                to={`/shape-up/bets/${group.betId}`}
                variant="body2"
                sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
              >
                {group.betName}
              </Link>
              <Stack spacing={0.5}>
                {group.reviews.map((review) => {
                  const { color, label } = prChipStyle({
                    state: 'open',
                    isDraft: false,
                    needsYourReview: true,
                    reviewState: review.reviewState,
                    checksState: review.checksState,
                  });
                  return (
                    <Box
                      key={review.prId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                      }}
                    >
                      <Tooltip title={prStateSummary({
                        state: 'open',
                        isDraft: false,
                        reviewState: review.reviewState,
                        checksState: review.checksState,
                      })}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={color}
                          icon={<CallSplitIcon sx={{ fontSize: 13 }} />}
                          label={`#${review.number} ${label}`}
                          component="a"
                          href={review.url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          onClick={(e) => e.stopPropagation()}
                          sx={{ flexShrink: 0 }}
                        />
                      </Tooltip>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {review.title}
                      </Typography>
                      {review.scopeJiraKey && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={review.scopeJiraKey}
                          component={RouterLink}
                          to={`/shape-up/bets/${review.betId}?scope=${review.scopeId}`}
                          clickable
                          sx={{
                            cursor: 'pointer',
                            flexShrink: 0,
                            maxWidth: 120,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
